// Finanz-Planer (admin-only) – Supabase-CRUD + Hochrechnungs-Logik.
// Daten liegen in finance_items / finance_settings (RLS: nur Admin).
//
// Seit 23.09.2026 (Umbau nach Design „Ampel und Kurve"):
// - Keine Potenziale mehr. Jeder Posten hat nur den Haken `active`
//   („mit einrechnen"). Was nicht angehakt ist, bleibt stehen, zählt aber nicht.
// - `start_balance` ist der Kontostand HEUTE (Stand `balance_date`). Die
//   Hochrechnung beginnt mit dem NÄCHSTEN Monat — der laufende Monat wird nicht
//   mehr komplett dazugerechnet (das zählte schon Eingegangenes doppelt).

import { supabase } from './supabase';

export type FinanceScope = 'business' | 'private';
export type FinanceType = 'fixed_cost' | 'recurring_income' | 'planned_income' | 'planned_expense' | 'tax_payment';
export type FinanceView = FinanceScope | 'all';

export type FinanceInterval = 'monthly' | 'quarterly' | 'yearly';

export const intervalLabels: Record<FinanceInterval, string> = {
  monthly: 'monatlich',
  quarterly: 'quartalsweise',
  yearly: 'jährlich',
};

export interface FinanceItem {
  id: string;
  scope: FinanceScope;
  type: FinanceType;
  label: string;
  amount: number; // Cent
  /** monatlich oder jährlich — nur für recurring_income/fixed_cost relevant.
      Bei yearly fällt der Betrag im Monat von start_month an, dann alle 12 Monate. */
  interval: FinanceInterval;
  start_month: string | null; // 'YYYY-MM-DD'
  /** Enddatum für wiederkehrende Posten (inklusive). NULL = läuft unbegrenzt. */
  end_month: string | null;
  installments: number;
  /** Der Haken „mit einrechnen". Aus = bleibt stehen, zählt nirgends. */
  active: boolean;
  sort_order: number;
}

export interface FinanceSettings {
  tax_rate: number; // Prozent
  horizon_months: number;
  /** Cent – Kontostand HEUTE über alle Konten (Stand balance_date). */
  start_balance: number;
  /** 'YYYY-MM-DD' — wann der Kontostand abgelesen wurde. */
  balance_date: string | null;
  /** Cent – was auf dem Steuerrücklagen-Konto wirklich liegt. Steckt im
   *  Kontostand, zählt aber nicht als frei verfügbar. */
  reserve_balance: number;
  /** USt automatisch aus den Geschäfts-Einnahmen rechnen (Durchlaufposten). */
  vat_enabled: boolean;
  vat_rate: number; // Prozent, i. d. R. 19
  /** Geschätzte abziehbare Vorsteuer je Monat in Cent (Pauschale). */
  vat_input_monthly: number;
  /** Zahlrhythmus der Voranmeldung: quartalsweise (Zahlung Jan/Apr/Jul/Okt) oder monatlich. */
  vat_interval: 'monthly' | 'quarterly';
  /** Umsatzsteuer, die dem Finanzamt heute schon gehört (Cent): vereinnahmt,
   *  noch nicht abgeführt. Steckt im Kontostand und wird deshalb vom frei
   *  Verfügbaren abgezogen; im nächsten Zahlmonat geht sie raus. */
  vat_pot_start: number;
}

export async function getFinanceItems(): Promise<FinanceItem[]> {
  const { data, error } = await supabase
    .from('finance_items')
    .select('*')
    .order('scope', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data || []) as FinanceItem[];
}

/** Netzwerk-/Timeout-Fehler in eine verständliche Meldung übersetzen. */
function normalizeWriteError(e: unknown): Error {
  const msg = e instanceof Error ? e.message : String(e);
  if (/abort|timed?\s?out|timeout|failed to fetch|networkerror/i.test(msg)) {
    return new Error('Zeitüberschreitung beim Speichern. Bitte Seite neu laden (Cmd+Shift+R) und erneut versuchen.');
  }
  return new Error(msg);
}

/**
 * Harte client-seitige Zeitgrenze um einen (thenable) Supabase-Aufruf. Fängt
 * AUCH ein Steckenbleiben ab, das der fetch-Timeout nicht sieht — z. B. wenn
 * `getSession()` beim Lock-Acquire hängt (kein Netz-Request). Nach dem Timeout
 * rejectet das Promise → der Button hört garantiert auf zu laden.
 */
function withTimeout<T>(p: PromiseLike<T>, ms = 22_000): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms);
    Promise.resolve(p).then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

export async function upsertFinanceItem(item: Partial<FinanceItem>): Promise<FinanceItem> {
  try {
    const { data, error } = await withTimeout(supabase.from('finance_items').upsert(item).select().single());
    if (error) throw new Error(error.message);
    return data as FinanceItem;
  } catch (e) {
    throw normalizeWriteError(e);
  }
}

/**
 * Nur den Haken setzen. BEWUSST ein UPDATE, kein Upsert: Ein Upsert prüft die
 * NOT-NULL-Spalten der Einfüge-Hälfte, BEVOR der Konflikt greift — ein
 * Teil-Payload ohne label scheiterte damit schon einmal (21.08.2026).
 */
export async function setFinanceItemActive(id: string, active: boolean): Promise<void> {
  try {
    const { error } = await withTimeout(
      supabase.from('finance_items').update({ active, updated_at: new Date().toISOString() }).eq('id', id)
    );
    if (error) throw new Error(error.message);
  } catch (e) {
    throw normalizeWriteError(e);
  }
}

/**
 * Mehrere Posten in EINEM Request schreiben — Grundlage des CSV-Imports.
 *
 * Posten MIT id werden aktualisiert, Posten OHNE id neu angelegt. Nicht
 * uebergebene Spalten (sort_order, created_at) bleiben unangetastet, weil
 * Supabase im ON-CONFLICT nur die mitgeschickten Felder setzt. Bewusst ein
 * Request: Ein halb durchgelaufener Import waere schlimmer als gar keiner.
 */
export async function bulkUpsertFinanceItems(items: Array<Partial<FinanceItem>>): Promise<number> {
  if (!items.length) return 0;
  try {
    const { data, error } = await withTimeout(
      supabase.from('finance_items').upsert(items).select('id'),
      40_000,
    );
    if (error) throw new Error(error.message);
    return (data || []).length;
  } catch (e) {
    throw normalizeWriteError(e);
  }
}

export async function deleteFinanceItem(id: string): Promise<void> {
  try {
    const { error } = await withTimeout(supabase.from('finance_items').delete().eq('id', id));
    if (error) throw new Error(error.message);
  } catch (e) {
    throw normalizeWriteError(e);
  }
}

export async function getFinanceSettings(): Promise<FinanceSettings> {
  const { data } = await supabase
    .from('finance_settings')
    .select('tax_rate, horizon_months, start_balance, balance_date, reserve_balance, vat_enabled, vat_rate, vat_input_monthly, vat_interval, vat_pot_start')
    .eq('key', 'default')
    .single();
  return {
    tax_rate: Number(data?.tax_rate ?? 30),
    horizon_months: Number(data?.horizon_months ?? 12),
    start_balance: Number(data?.start_balance ?? 0),
    balance_date: data?.balance_date ?? null,
    reserve_balance: Number(data?.reserve_balance ?? 0),
    vat_enabled: Boolean(data?.vat_enabled ?? false),
    vat_rate: Number(data?.vat_rate ?? 19),
    vat_input_monthly: Number(data?.vat_input_monthly ?? 0),
    vat_interval: (data?.vat_interval === 'monthly' ? 'monthly' : 'quarterly'),
    vat_pot_start: Number(data?.vat_pot_start ?? 0),
  };
}

export async function saveFinanceSettings(s: FinanceSettings): Promise<void> {
  try {
    const { error } = await withTimeout(supabase
      .from('finance_settings')
      .update({
        tax_rate: s.tax_rate, horizon_months: s.horizon_months,
        start_balance: s.start_balance, balance_date: s.balance_date, reserve_balance: s.reserve_balance,
        vat_enabled: s.vat_enabled, vat_rate: s.vat_rate, vat_input_monthly: s.vat_input_monthly,
        vat_interval: s.vat_interval, vat_pot_start: s.vat_pot_start,
        updated_at: new Date().toISOString(),
      })
      .eq('key', 'default'));
    if (error) throw new Error(error.message);
  } catch (e) {
    throw normalizeWriteError(e);
  }
}

// ── Hochrechnung ───────────────────────────────────────────────────────────

export interface MonthProjection {
  key: string; // 'YYYY-MM'
  label: string; // 'Okt 26'
  labelLong: string; // 'Oktober 2026'
  income: number; // Cent (recurring + planned)
  fixedCosts: number;
  taxReserve: number;
  /** Steuerzahlungen dieses Monats — aus dem Topf, nicht aus dem Cashflow. */
  taxPaid: number;
  net: number; // Überschuss (nach Steuerrücklage)
  cumulative: number; // Σ Überschuss (Veränderung ggü. Start)
  balance: number; // Kontostand gesamt = frei verfügbar + Steuer-Topf + USt-Topf
  available: number; // frei verfügbar = Start − Töpfe + Σ Überschuss
  reservePot: number; // Steuerrücklage-Topf (Rücklagenkonto + Σ Rücklage − Zahlungen)
  /** USt dieses Monats: vereinnahmt (Satz × Geschäfts-Einnahmen − Vorsteuer-Pauschale). */
  vatAccrued: number;
  /** USt-Zahlung ans Finanzamt in diesem Monat (aus dem USt-Topf; negativ = Erstattung). */
  vatPaid: number;
  /** USt-Topf am Monatsende — vereinnahmt, aber noch nicht abgeführt. */
  vatPot: number;
  /** Laufen in diesem Monat noch Projektraten oder Einmalposten? */
  hasPlanned: boolean;
}

export function absMonth(d: Date): number {
  return d.getFullYear() * 12 + d.getMonth();
}

/** Erster Monat der Hochrechnung: der NÄCHSTE Monat (der Kontostand gilt heute). */
export function projectionBase(): number {
  return absMonth(new Date()) + 1;
}

/** Fällt ein wiederkehrender Posten (monatlich/quartalsweise/jährlich, Ab/Bis) in diesem Monat an? */
function wiederkehrtIn(it: FinanceItem, targetAbs: number): boolean {
  const startAbs = it.start_month ? absMonth(new Date(it.start_month)) : null;
  const endAbs = it.end_month ? absMonth(new Date(it.end_month)) : null;
  if (endAbs !== null && targetAbs > endAbs) return false;
  if (it.interval === 'yearly' || it.interval === 'quarterly') {
    // Takt-Beträge fallen im Startmonat an und dann alle 3 bzw. 12 Monate.
    // Ohne Startmonat gibt es keinen Anker → zählt nie (UI/Import erzwingen ihn).
    if (startAbs === null || targetAbs < startAbs) return false;
    return (targetAbs - startAbs) % (it.interval === 'quarterly' ? 3 : 12) === 0;
  }
  return startAbs === null || targetAbs >= startAbs;
}

/** Monatswerte einer scope-Menge für einen absoluten Monat. */
function monthlyFor(items: FinanceItem[], scopes: FinanceScope[], targetAbs: number) {
  let income = 0;
  let fixedCosts = 0;
  let hasPlanned = false;
  for (const it of items) {
    if (!it.active || !scopes.includes(it.scope)) continue;
    if (it.type === 'fixed_cost') {
      if (wiederkehrtIn(it, targetAbs)) fixedCosts += it.amount;
    } else if (it.type === 'recurring_income') {
      if (wiederkehrtIn(it, targetAbs)) income += it.amount;
    } else if (it.type === 'planned_income' && it.start_month) {
      const s = absMonth(new Date(it.start_month));
      const n = Math.max(1, it.installments || 1);
      if (targetAbs >= s && targetAbs <= s + n - 1) { income += it.amount; hasPlanned = true; }
    } else if (it.type === 'planned_expense' && it.start_month) {
      const s = absMonth(new Date(it.start_month));
      const n = Math.max(1, it.installments || 1);
      if (targetAbs >= s && targetAbs <= s + n - 1) { fixedCosts += it.amount; hasPlanned = true; }
    }
    // tax_payment läuft bewusst NICHT hier mit: Die Zahlung kommt aus dem
    // Steuer-Topf (siehe computeProjection), nicht aus dem laufenden Cashflow.
  }
  return { income, fixedCosts, hasPlanned };
}

/** Steuerzahlungen (aus dem Topf) eines Monats — unabhängig vom scope,
    weil es nur EINEN Steuer-Topf gibt. */
function taxPaymentsFor(items: FinanceItem[], targetAbs: number): number {
  let sum = 0;
  for (const it of items) {
    if (!it.active || it.type !== 'tax_payment') continue;
    if (wiederkehrtIn(it, targetAbs)) sum += it.amount;
  }
  return sum;
}

function monthLabels(abs: number): { key: string; label: string; labelLong: string } {
  const d = new Date(Math.floor(abs / 12), abs % 12, 1);
  return {
    key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
    label: d.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' }),
    labelLong: d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
  };
}

/**
 * Hochrechnung über den Horizont, beginnend mit dem NÄCHSTEN Monat.
 * Steuerrücklage wird NUR auf den Geschäfts-Gewinn (Einnahmen − Fixkosten)
 * angewandt; privat nicht.
 *
 * Kontostand heute = frei verfügbar + Rücklagenkonto + USt-Topf. Die beiden
 * Töpfe stecken real im Kontostand, gehören aber nicht Nico — deshalb starten
 * sie als Töpfe und werden vom frei Verfügbaren abgezogen.
 */
export function computeProjection(items: FinanceItem[], settings: FinanceSettings, view: FinanceView): MonthProjection[] {
  const base = projectionBase();
  const taxRate = Math.max(0, settings.tax_rate) / 100;
  const startBalance = settings.start_balance || 0;
  const out: MonthProjection[] = [];
  let cumulative = 0;
  const reserveStart = view === 'private' ? 0 : (settings.reserve_balance || 0);
  let reservePot = reserveStart;
  const vatStart = settings.vat_enabled && view !== 'private' ? (settings.vat_pot_start || 0) : 0;
  let vatPot = vatStart;

  for (let m = 0; m < settings.horizon_months; m++) {
    const targetAbs = base + m;
    const biz = monthlyFor(items, ['business'], targetAbs);
    const priv = monthlyFor(items, ['private'], targetAbs);

    const bizNet = biz.income - biz.fixedCosts;
    const taxReserve = Math.round(Math.max(0, bizNet) * taxRate);

    let income = 0;
    let fixedCosts = 0;
    let reserve = 0;
    let hasPlanned = false;
    if (view === 'business') {
      income = biz.income; fixedCosts = biz.fixedCosts; reserve = taxReserve; hasPlanned = biz.hasPlanned;
    } else if (view === 'private') {
      income = priv.income; fixedCosts = priv.fixedCosts; reserve = 0; hasPlanned = priv.hasPlanned;
    } else {
      income = biz.income + priv.income; fixedCosts = biz.fixedCosts + priv.fixedCosts; reserve = taxReserve; hasPlanned = biz.hasPlanned || priv.hasPlanned;
    }

    // Steuerzahlungen: senken NUR den Topf (und damit den Kontostand),
    // nie das frei Verfügbare — die Rücklage wurde ja genau dafür gebildet.
    const taxPaid = view === 'private' ? 0 : taxPaymentsFor(items, targetAbs);

    // USt (Durchlaufposten): mit jeder Rechnung vereinnahmt, gesammelt im
    // USt-Topf, im Zahlmonat ans Finanzamt. Zahlmonate: Jan/Apr/Jul/Okt
    // (Monat nach Quartalsende) bzw. jeden Monat. Zahlung ZUERST (sie gilt
    // dem Vorzeitraum), dann läuft der neue Zeitraum an. Negativ = Erstattung.
    let vatAccrued = 0;
    let vatPaid = 0;
    if (settings.vat_enabled && view !== 'private') {
      const zahlmonat = settings.vat_interval === 'monthly' || targetAbs % 3 === 0;
      if (zahlmonat) { vatPaid = vatPot; vatPot = 0; }
      vatAccrued = Math.round((Math.max(0, settings.vat_rate) / 100) * biz.income) - (settings.vat_input_monthly || 0);
      vatPot += vatAccrued;
    }

    const net = income - fixedCosts - reserve;
    cumulative += net;
    reservePot += reserve - taxPaid;
    const available = startBalance + cumulative - vatStart - reserveStart;
    const balance = available + reservePot + vatPot;

    out.push({
      ...monthLabels(targetAbs),
      income, fixedCosts, taxReserve: reserve, taxPaid, net, cumulative, balance, available, reservePot,
      vatAccrued, vatPaid, vatPot, hasPlanned,
    });
  }
  return out;
}

// ── Monats-Detail (Drill-down) ───────────────────────────────────────────────

export interface MonthLine {
  id: string;
  label: string;
  scope: FinanceScope;
  type: FinanceType;
  amount: number; // Beitrag in diesem Monat (Cent)
  note?: string; // z. B. "Rate 2/3 (letzte)", "ab diesem Monat", "monatlich"
}

export interface MonthDetail {
  incomes: MonthLine[];
  costs: MonthLine[];
  /** Steuerzahlungen aus dem Topf — mindern Kontostand+Topf, nie das frei Verfügbare. */
  taxPayments: MonthLine[];
  incomeTotal: number;
  costTotal: number;
  bizProfit: number; // Basis der Steuerrücklage: business-Einnahmen − business-Fixkosten
  taxReserve: number;
  net: number;
}

/**
 * Zerlegt einen einzelnen Monat (monthIndex ab dem ersten Hochrechnungsmonat
 * = 0) in die konkret beitragenden Posten. Deckungsgleich mit
 * computeProjection, nur eben mit Auflistung statt Summe.
 */
export function monthDetail(
  items: FinanceItem[],
  settings: FinanceSettings,
  view: FinanceView,
  monthIndex: number,
): MonthDetail {
  const targetAbs = projectionBase() + monthIndex;
  const taxRate = Math.max(0, settings.tax_rate) / 100;
  const scopes: FinanceScope[] = view === 'all' ? ['business', 'private'] : [view];

  const incomes: MonthLine[] = [];
  const costs: MonthLine[] = [];
  const taxPayments: MonthLine[] = [];
  let bizIncome = 0;
  let bizFixed = 0;

  for (const it of items) {
    if (!it.active) continue;
    const inView = scopes.includes(it.scope);
    const startAbs = it.start_month ? absMonth(new Date(it.start_month)) : null;

    const taktNote = it.interval === 'yearly' ? 'jährlich' : it.interval === 'quarterly' ? 'quartalsweise' : null;
    if (it.type === 'recurring_income') {
      if (!wiederkehrtIn(it, targetAbs)) continue;
      if (it.scope === 'business') bizIncome += it.amount;
      if (inView) incomes.push({
        id: it.id, label: it.label, scope: it.scope, type: it.type, amount: it.amount,
        note: taktNote ?? (startAbs !== null && targetAbs === startAbs ? 'ab diesem Monat' : 'wiederkehrend'),
      });
    } else if (it.type === 'fixed_cost') {
      if (!wiederkehrtIn(it, targetAbs)) continue;
      if (it.scope === 'business') bizFixed += it.amount;
      if (inView) costs.push({
        id: it.id, label: it.label, scope: it.scope, type: it.type, amount: it.amount,
        note: taktNote ?? (startAbs !== null && targetAbs === startAbs ? 'ab diesem Monat' : 'monatlich'),
      });
    } else if (it.type === 'tax_payment') {
      if (view === 'private' || !wiederkehrtIn(it, targetAbs)) continue;
      taxPayments.push({
        id: it.id, label: it.label, scope: it.scope, type: it.type, amount: it.amount,
        note: taktNote ?? 'einmalig',
      });
    } else if (it.type === 'planned_income' && it.start_month) {
      const s = absMonth(new Date(it.start_month));
      const n = Math.max(1, it.installments || 1);
      if (targetAbs < s || targetAbs > s + n - 1) continue;
      const rateNo = targetAbs - s + 1;
      if (it.scope === 'business') bizIncome += it.amount;
      if (inView) incomes.push({
        id: it.id, label: it.label, scope: it.scope, type: it.type, amount: it.amount,
        note: n > 1 ? `Rate ${rateNo}/${n}${rateNo === n ? ' (letzte)' : ''}` : 'Einmalzahlung',
      });
    } else if (it.type === 'planned_expense' && it.start_month) {
      const s = absMonth(new Date(it.start_month));
      const n = Math.max(1, it.installments || 1);
      if (targetAbs < s || targetAbs > s + n - 1) continue;
      const rateNo = targetAbs - s + 1;
      if (it.scope === 'business') bizFixed += it.amount;
      if (inView) costs.push({
        id: it.id, label: it.label, scope: it.scope, type: it.type, amount: it.amount,
        note: n > 1 ? `Rate ${rateNo}/${n}${rateNo === n ? ' (letzte)' : ''}` : 'Einmalzahlung',
      });
    }
  }

  const incomeTotal = incomes.reduce((s, l) => s + l.amount, 0);
  const costTotal = costs.reduce((s, l) => s + l.amount, 0);
  const bizProfit = bizIncome - bizFixed;
  const taxReserve = view === 'private' ? 0 : Math.round(Math.max(0, bizProfit) * taxRate);
  return {
    incomes: incomes.sort((a, b) => b.amount - a.amount),
    costs: costs.sort((a, b) => b.amount - a.amount),
    taxPayments,
    incomeTotal, costTotal, bizProfit, taxReserve,
    net: incomeTotal - costTotal - taxReserve,
  };
}

export function euro(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(
    (cents || 0) / 100
  );
}

export const typeLabels: Record<FinanceType, string> = {
  fixed_cost: 'Fixkosten',
  recurring_income: 'Wiederkehrende Einnahmen',
  planned_income: 'Geplante Einnahmen',
  planned_expense: 'Geplante Ausgaben',
  tax_payment: 'Steuerzahlungen (aus Steuer-Topf)',
};

export const scopeLabels: Record<FinanceScope, string> = {
  business: 'Geschäftlich',
  private: 'Privat',
};
